import { useSetting } from '@ui/hooks'
import { useEffect, useState, useRef } from 'react'
import css from 'styled-jsx/css'

type InlineBannerProps = {
  open?: boolean
  isUserScrolling?: boolean
  percentOff: number
  countdownText?: string
  className?: string
}

export const InlineBanner: React.FC<InlineBannerProps> = ({
  open,
  isUserScrolling,
  percentOff,
  countdownText,
  className
}) => {
  const [visible, setVisible] = useState(open)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [storeSetting] = useSetting('store')
  const version = storeSetting?.version
  const isMobile = process?.browser && window?.innerWidth <= 768

  useEffect(() => {
    if (
      (version == 'f25v52' ||
        version == 'f25v612' ||
        version == 'f25v20' ||
        version == 'f25v611' ||
        version == 'f25v61') &&
      open
    ) {
      setVisible(open)
    }
  }, [open, version])

  const handleScrolling = () => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        if (isUserScrolling) {
          setTimeout(() => {
            setVisible(false)
          }, 3000)
        } else {
          setVisible(false)
        }
      }
    }

    const handleScroll = () => {
      if (isUserScrolling) {
        setTimeout(() => {
          setVisible(false)
        }, 3000)
      } else {
        setVisible(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    document.addEventListener('wheel', handleScroll, { passive: true })
    document.addEventListener('touchmove', handleScroll, { passive: true })

    return () => {
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('wheel', handleScroll)
      document.removeEventListener('touchmove', handleScroll)
    }
  }

  const handleScrollToPosition = () => {
    isMobile ? handleScrollToPrice() : handleScrollToTop()
    setVisible(false)
  }

  useEffect(() => {
    if (visible) handleScrolling()
  }, [visible])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    if (
      (version === 'f25v612' ||
        version === 'f25v52' ||
        version === 'f25v61' ||
        version === 'f25v611' ||
        version === 'f25v20') &&
      visible &&
      isMobile &&
      !isUserScrolling
    ) {
      timeoutId = setTimeout(() => {
        handleScrollToPrice()
      }, 0)
    }
    return () => {
      clearTimeout(timeoutId)
    }
  }, [visible, version])

  return (
    <>
      <style jsx>{CountdownNotifyModalStyles}</style>
      <div
        ref={wrapperRef}
        className={`clearance-wrapper ${className ? className : ''} popup-clearance ${
          visible ? ' is-active' : ''
        }`}
      >
        <div className="clearance-modal">
          <div className="clearance-modal__nav">
            <span>You’ve got the best clearance deal!</span>
          </div>
          <div className="clearance-content">
            <div className="clearance-content__title">
              {countdownText === 'Christmas Sale' ? (
                <>
                  BIGGEST HOLIDAY <br className="is-hidden-tablet" /> SALE -{' '}
                  {Math.floor(percentOff)}% OFF!
                </>
              ) : (
                <>
                  CLEARANCE SALE <br className="is-hidden-tablet" /> {Math.floor(percentOff)}% OFF!
                </>
              )}
            </div>
            <div className="clearance-content__desc">
              Discounts starting from {Math.floor(percentOff)}% have already been applied to this
              product
            </div>
          </div>
          <div className="clearance-modal__btn">
            <button
              className="clearance-modal__btn--content"
              onClick={() => {
                handleScrollToPosition()
              }}
            >
              Don&apos;t Wait, Shop Now!
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export const handleScrollToPrice = () => {
  const priceElement = document.querySelector('.product__price') as HTMLDivElement
  const countdownNotifyElements = document.querySelector('.countdown-notify') as HTMLDivElement
  const priceElementHeight = priceElement?.offsetHeight || 0
  const countdownNotifyElementsHeight = countdownNotifyElements?.offsetHeight || 0
  const heightOfWindow = window.innerHeight || 0
  const rect = priceElement.getBoundingClientRect()
  const modalOffset = 2
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const scrollPosition =
    rect.y -
    (heightOfWindow - priceElementHeight - countdownNotifyElementsHeight) +
    scrollTop -
    modalOffset
  window.scrollTo({ top: scrollPosition, behavior: 'smooth' })
}

export const handleScrollToTop = () => {
  const headerElement = document.querySelector('.header--primary') as HTMLDivElement
  const rect = headerElement.getBoundingClientRect()
  const modalOffset = 15
  const scrollPosition = rect.height + modalOffset
  window.scrollTo({ top: scrollPosition, behavior: 'smooth' })
}

const CountdownNotifyModalStyles = css`
  .clearance-wrapper {
    &.popup-clearance {
      position: fixed;
      opacity: 0;
      bottom: -1px;
      left: 0;
      right: 0;
      z-index: -1;
      width: 100% !important;
      height: fit-content;
      box-shadow: 0px -10px 10px -4px #6573814f;
      .clearance-modal {
        background-color: #e8f5e9;
        padding-bottom: 17px;
        &__nav {
          height: 40px;
          background-color: #1d9f54;
          padding: 8px 14px;
          span {
            font-weight: 700;
            font-size: 16px;
            line-height: 100%;
            color: #ffffff;
            letter-spacing: -0.16px;
          }
        }
        .clearance-content {
          padding: 12px 13px 8px 13px;
          background-color: #e8f5e9;

          &__title {
            font-weight: 700;
            font-size: 32px;
            line-height: 40px;
            color: #1d9f54;
            text-transform: uppercase;
            letter-spacing: -0.64px;
          }

          &__desc {
            margin-top: 2px;
            font-weight: 400;
            font-size: 16px;
            line-height: 26px;

            color: #363636;
          }
        }
        &__btn {
          margin: 0 13px;
          &--content {
            cursor: pointer;
            border-radius: 4px;
            height: 52px;
            width: 100%;
            background-color: #282828;
            border: 0;
            outline: 0;
            color: #ffffff;
            font-size: 16px;
            line-height: 24px;
            font-weight: 600;
            padding: 0;
          }
        }
      }
    }
    @media screen and (min-width: 375px) {
      &.popup-clearance {
        opacity: 0;
        z-index: -1;
        .clearance-modal {
          background-color: #e8f5e9;
          padding-bottom: 17px;
          &__nav {
            height: 40px;
            background-color: #1d9f54;
            padding: 8px 14px;
            span {
              font-weight: 700;
              font-size: 16px;
              line-height: 100%;
              color: #ffffff;
              letter-spacing: -0.16px;
            }
          }
          .clearance-content {
            padding: 12px 15px 8px 15px;
            background-color: #e8f5e9;

            &__title {
              font-weight: 700;
              font-size: 32px;
              line-height: 40px;
              color: #1d9f54;
              text-transform: uppercase;
              letter-spacing: -0.64px;
            }

            &__desc {
              margin-top: 2px;
              font-weight: 400;
              font-size: 16px;
              line-height: 26px;

              color: #363636;
            }
          }
          &__btn {
            margin: 0 15px;
            &--content {
              cursor: pointer;
              border-radius: 4px;
              height: 52px;
              width: 100%;
              background-color: #282828;
              border: 0;
              outline: 0;
              color: #ffffff;
              font-size: 16px;
              line-height: 24px;
              font-weight: 600;
              padding: 0;
            }
          }
        }
      }
    }

    @media screen and (min-width: 769px) {
      &.popup-clearance {
        opacity: 0;
        position: fixed;
        z-index: -1;
        top: 0;
        left: 0;
        right: 0;
        width: 100% !important;
        background-color: #e8f5e9;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0px 1.2px 9.2px 0px #65738133;
      }
      .clearance-modal {
        width: 100% !important;
        margin: 24px 17.5px !important;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-bottom: 0 !important;
        &__nav {
          display: none;
        }
        .clearance-content {
          padding: 0 !important;
          &__title {
            width: 100%;
            font-weight: 700;
            font-size: 32px !important;
            line-height: 38px !important;
            white-space: nowrap;
          }

          &__desc {
            margin-top: 4px !important;
            font-size: 15px !important;
            line-height: 24px !important;
          }
        }
        &__btn {
          margin: 0 !important;
          &--content {
            height: 50px !important;
            padding: 13px 14px !important;
            white-space: nowrap;
            font-size: 14px !important;
            line-height: 24px !important;
          }
        }
      }
    }

    @media screen and (min-width: 1024px) {
      &.popup-clearance {
        position: fixed;
        opacity: 0;
        top: 0;
        left: 0;
        right: 0;
        z-index: -1;
        width: 100% !important;
        background-color: #e8f5e9;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0px 1.2px 9.2px 0px #65738133;
      }
      .clearance-modal {
        max-width: 933px;
        margin: 24px 0px !important;
        display: flex;
        align-items: center;
        gap: 80px;
        padding-bottom: 0 !important;
        &__nav {
          display: none;
        }
        .clearance-content {
          padding: 0 !important;
          &__title {
            width: 100%;
            font-size: 38px !important;
            line-height: 50px !important;
            white-space: nowrap;
            letter-spacing: -0.76px !important;
          }

          &__desc {
            margin-top: 4px !important;
            font-size: 18px !important;
            line-height: 26px !important;
          }
        }
        &__btn {
          margin: 0 !important;
          &--content {
            height: 58px !important;
            padding: 17px 20px;
            white-space: nowrap;
            width: 218px !important;
            font-size: 16px !important;
          }
        }
      }
    }
    &.is-active {
      opacity: 1;
      transition: opacity 0.3s ease-in-out;
      z-index: 30;
    }
  }
`
